import { FC } from 'react'
import tw from 'twin.macro'
import { ImageField } from '@prismicio/client'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import Carousel from '../Carousel'
import { Body, Grid } from '../UI'
import { theme } from '../../../tailwind.config'
import { PrismicRichText } from '@prismicio/react'
import { richTextComponents } from '../../utils/richTextComponents'
import { RichTextFields } from '../../types/prismic'

type Props = {
  header: string
  subhead: RichTextFields
  testimonials: Array<{
    copy: string
    author_name: string
    author_age: number
    image?: ImageField
  }>
  background_color: 'gb-cream' | 'white'
}

type CommunityCarouselProps = {
  index: number
  copy: string
  author_name: string
  author_age: number
  image: ImageField
}

const CommunityCarouselComponent: FC<CommunityCarouselProps> = ({
  index,
  copy,
  author_name,
  author_age,
  image,
}) => {
  const fetchedImage = getImage(image)
  return (
    <div
      css={[
        tw`flex flex-col justify-between mx-[10px] lg:mx-[25px] border-[27px] lg:border-[36px] w-[80%] lg:w-[30%] min-h-full relative bg-white p-7`,
        index % 3 === 0 && tw`border-gb-red`,
        index % 3 === 1 && tw`border-gb-magenta`,
        index % 3 === 2 && tw`border-gb-blue`,
      ]}
    >
      <p
        css={[
          tw`font-normal line-height[22px]`,
          fetchedImage ? tw`text-[14px] mb-2` : tw`text-[16px] mb-8`,
        ]}
      >
        &ldquo;{copy}&rdquo;
      </p>
      <div tw="flex flex-row justify-between items-end">
        <div css={[Body, tw`line-height[22px]`]}>
          <span tw="font-object uppercase">{author_name}</span>,&nbsp;
          <span tw="font-domaine">{author_age}</span>
        </div>
        {fetchedImage && (
          <GatsbyImage
            image={fetchedImage}
            alt={author_name}
            tw="h-[100px] w-[100px] rounded-full"
          />
        )}
      </div>
    </div>
  )
}

const Community: FC<Props> = ({
  header,
  subhead,
  testimonials,
  background_color,
}) => {
  return (
    <section
      id="community"
      css={[
        { backgroundColor: theme.extend.colors[background_color] || 'white' },
        tw`pt-[55px] pb-[120px] lg:(pt-[82px] pb-[91px]) text-gb-blue font-domaine`,
      ]}
    >
      <Grid tw="mx-0">
        {header && (
          <div tw="col-span-full pl-6 lg:pl-0 lg:(col-span-5)">
            <h2 css={[Body, tw`mb-[15px] lg:mb-[25px] `]}>{header}</h2>
          </div>
        )}
        {subhead && (
          <div tw="col-span-full mb-8 pl-6 lg:pl-0  lg:(col-start-1 col-span-full mb-5 pr-6)">
            <PrismicRichText
              field={subhead.richText}
              components={richTextComponents}
            />
          </div>
        )}

        <div tw="col-span-full">
          <Carousel
            data={testimonials}
            ItemComponent={CommunityCarouselComponent}
            initialIndexSmall={0}
            initialIndexLarge={1}
            controls="regular"
          />
        </div>
      </Grid>
    </section>
  )
}

export default Community
